/**
 * Country Selector Component
 * Dropdown to switch between Canada and USA
 */

import React from 'react';
import { useCountry, Country } from '../../contexts/CountryContext';

interface CountrySelectorProps {
  variant?: 'dropdown' | 'flags' | 'compact';
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ variant = 'flags' }) => {
  const { country, setCountry } = useCountry();

  const countries: { code: Country; name: string; flag: string }[] = [
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'US', name: 'USA', flag: '🇺🇸' },
  ];

  if (variant === 'dropdown') {
    return (
      <select
        value={country}
        onChange={(e) => setCountry(e.target.value as Country)}
        style={{
          padding: '6px 12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          background: 'white',
          fontSize: '0.9rem',
          cursor: 'pointer',
        }}
        aria-label="Select country"
      >
        {countries.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.name}
          </option>
        ))}
      </select>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={() => setCountry(country === 'CA' ? 'US' : 'CA')}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.2rem',
          padding: '4px 8px',
        }}
        title={`Switch to ${country === 'CA' ? 'USA' : 'Canada'}`}
        aria-label={`Currently ${country === 'CA' ? 'Canada' : 'USA'}. Click to switch.`}
      >
        {country === 'CA' ? '🇨🇦' : '🇺🇸'}
      </button>
    );
  }

  // Default: flags variant - compact text toggle (Amazon currency selector style)
  return (
    <button
      onClick={() => setCountry(country === 'CA' ? 'US' : 'CA')}
      style={{
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '6px',
        padding: '4px 10px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        transition: 'background 0.2s',
        lineHeight: 1,
      }}
      title={`Switch to ${country === 'CA' ? 'USA' : 'Canada'}`}
      aria-label={`Currently ${country === 'CA' ? 'Canada' : 'USA'}. Click to switch.`}
    >
      <span style={{
        fontSize: '0.85rem',
        fontWeight: country === 'CA' ? 700 : 400,
        color: country === 'CA' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
      }}>
        CA
      </span>
      <span style={{
        fontSize: '0.85rem',
        color: 'rgba(255, 255, 255, 0.4)',
        margin: '0 1px',
      }}>
        /
      </span>
      <span style={{
        fontSize: '0.85rem',
        fontWeight: country === 'US' ? 700 : 400,
        color: country === 'US' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
      }}>
        US
      </span>
    </button>
  );
};

export default CountrySelector;
